import {
  CanonicalShortTermConditionLayerKind,
  withCanonicalShortTermConditionLayerMetadata,
} from 'features/dataLayers/canonicalShortTermConditionLayers'
import { withReusableWorkflowGeneratedCdfMetadata } from 'features/dataLayers/generatedCdfVisibility'
import {
  createTminSource,
  type TminSource,
} from 'features/dataLayers/generation/calculations/calculateDerivedValues'
import { useGenerateLossByPercentageCDFs } from 'features/dataLayers/generation/coreLayers/wastage/generateLossByPercentageCDFs'
import {
  getCdfResolutionKey,
  getComparableShortTermRateInspectionCdfGroups,
  getInspectionCdfsForResolutionKey,
  getShortTermRateInspectionCdfGroups,
} from 'features/dataLayers/generation/shortTermCorrosionRate/inspectionChoices'
import { getShiftedRectangleCellBounds } from 'features/dataLayers/generation/shortTermCorrosionRate/priorMatching'
import type {
  ShortTermCorrosionFeatureRectangle,
  ShortTermCorrosionPriorSupport,
} from 'features/dataLayers/generation/shortTermCorrosionRate/types'
import {
  DEFAULT_SHORT_TERM_CORROSION_RATE_GENERATION_PARAMS,
  type GeneratedShortTermCorrosionRateCdfResult,
  type GeneratedShortTermCorrosionWorkflowCdfResult,
  type ShortTermCorrosionRateGenerationParams,
  useCalculateShortTermCorrosionRateCdf,
  useCalculateShortTermRemainingLifeCdf,
  useDetectShortTermCorrosionFeatureRectangles,
  useMatchShortTermCorrosionFeatureRectangles,
} from 'features/dataLayers/generation/shortTermCorrosionRate/useGenerateShortTermCorrosionRateCDFs'
import {
  findSessionShortTermCorrosionCdf,
  type ShortTermCorrosionWorkflowModel,
  type ShortTermCorrosionWorkflowReferenceData,
} from 'features/dataLayers/generation/shortTermCorrosionRate/workflow/useShortTermCorrosionWorkflowModel'
import { usePersistGeneratedCdf } from 'features/dataLayers/generation/usePersistGeneratedCdf'
import { useGetForecastComponentConfig } from 'features/fde/componentConfigAPI'
import { getTMinMethod } from 'features/fde/tminMethod/tminMethodCallback'
import { useGeckoLocale } from 'features/locale'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  type GeckoComponentCoords,
  type GeckoComponentDataFrame,
  type GeckoComponentDeficiency,
  type GeckoInspection,
  isInspectionThicknessCDF,
} from 'types'

import {
  getFeatureGridMetadata,
  getObservationFeatureRectangles,
} from './featureMarkup'
import { getWorkflowDisplayUnits } from './ParameterControls'
import {
  getShortTermWorkflowFeatureViewState,
  getShortTermWorkflowPrimaryAction,
  ShortTermCorrosionWorkflowPanelView,
} from './ShortTermCorrosionWorkflowPanelView'
import { type AsyncStatus, WizardStep } from './WizardPanels'
import type { ShortTermCorrosionWorkflowVisualizationReferenceData } from './WorkflowVizPanel'

export type ShortTermCorrosionWorkflowPanelReferenceData =
  ShortTermCorrosionWorkflowReferenceData &
    ShortTermCorrosionWorkflowVisualizationReferenceData & {
      inspections: readonly GeckoInspection[]
    }

export interface ShortTermCorrosionWorkflowBaseProps {
  referenceData: ShortTermCorrosionWorkflowPanelReferenceData
  workflowModel: ShortTermCorrosionWorkflowModel
  componentDeficiencies: readonly GeckoComponentDeficiency[]
  getReusableWorkflowLayerName: (cdf: GeckoComponentDataFrame) => string
  initialComponentId?: string
  initialTargetInspectionId?: string
  initialTargetCdfId?: string
  onGenerated: (result: GeneratedShortTermCorrosionWorkflowCdfResult) => void
  onRateComplete: (result: GeneratedShortTermCorrosionRateCdfResult) => void
}

export interface ShortTermCorrosionWorkflowPanelProps extends ShortTermCorrosionWorkflowBaseProps {
  active: boolean
  onExit: () => void
}

export function ShortTermCorrosionWorkflowPanel({
  active,
  onExit,
  referenceData,
  workflowModel,
  componentDeficiencies,
  getReusableWorkflowLayerName,
  initialComponentId,
  initialTargetInspectionId,
  initialTargetCdfId,
  onGenerated,
  onRateComplete,
}: ShortTermCorrosionWorkflowPanelProps) {
  const detectFeatureRectangles = useDetectShortTermCorrosionFeatureRectangles()
  const matchFeatureRectangles = useMatchShortTermCorrosionFeatureRectangles()
  const calculateRateCdf = useCalculateShortTermCorrosionRateCdf()
  const calculateRemainingLifeCdf = useCalculateShortTermRemainingLifeCdf()
  const persistGeneratedCdf = usePersistGeneratedCdf()
  const { measurementSystem } = useGeckoLocale()
  const wasOpenRef = useRef(false)
  const lastResetSignatureRef = useRef<string | undefined>(undefined)
  const [componentId, setComponentId] = useState<string>()
  const [targetInspectionId, setTargetInspectionId] = useState<string>()
  const [targetCdfId, setTargetCdfId] = useState<string>()
  const [comparisonInspectionId, setComparisonInspectionId] = useState<string>()
  const [params, setParams] = useState<ShortTermCorrosionRateGenerationParams>(
    DEFAULT_SHORT_TERM_CORROSION_RATE_GENERATION_PARAMS
  )
  const [step, setStep] = useState<WizardStep>(WizardStep.Features)
  const [featureRectangles, setFeatureRectangles] = useState<
    ShortTermCorrosionFeatureRectangle[]
  >([])
  const [priorSupports, setPriorSupports] = useState<
    ShortTermCorrosionPriorSupport[]
  >([])
  const [algorithmPriorSupports, setAlgorithmPriorSupports] = useState<
    ShortTermCorrosionPriorSupport[]
  >([])
  const [selectedFeatureId, setSelectedFeatureId] = useState<string>()
  const [hasDetected, setHasDetected] = useState(false)
  const [hasMatched, setHasMatched] = useState(false)
  const [asyncStatus, setAsyncStatus] = useState<AsyncStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [lastRateResult, setLastRateResult] =
    useState<GeneratedShortTermCorrosionRateCdfResult | null>(null)
  const [hoveredCoords, setHoveredCoords] = useState<GeckoComponentCoords>()
  const displayUnits = useMemo(
    () => getWorkflowDisplayUnits(measurementSystem),
    [measurementSystem]
  )
  const targetGroups = useMemo(
    () =>
      getShortTermRateInspectionCdfGroups({
        componentId,
        componentDataFrames: workflowModel.componentDataFrames,
        inspections: referenceData.inspections,
        templateCdf: undefined,
      }),
    [componentId, referenceData.inspections, workflowModel.componentDataFrames]
  )
  const targetGroup = useMemo(
    () =>
      targetGroups.find((group) => group.inspectionId === targetInspectionId) ??
      targetGroups[0],
    [targetGroups, targetInspectionId]
  )
  const targetCdf = useMemo(() => {
    const selectedCdf = targetGroup?.cdfs.find((cdf) => cdf.id === targetCdfId)
    if (selectedCdf) return selectedCdf
    const initialCdf = targetGroup?.cdfs.find(
      (cdf) => cdf.id === initialTargetCdfId
    )
    return initialCdf ?? targetGroup?.cdfs[0]
  }, [initialTargetCdfId, targetCdfId, targetGroup])
  const comparisonGroups = useMemo(() => {
    if (!componentId || !targetCdf || !targetGroup) return []
    const targetTime = targetGroup.inspection.data.date.getTime()
    return getComparableShortTermRateInspectionCdfGroups({
      componentId,
      componentDataFrames: workflowModel.componentDataFrames,
      inspections: referenceData.inspections,
      templateCdf: targetCdf,
    }).filter((group) => group.inspection.data.date.getTime() < targetTime)
  }, [
    componentId,
    referenceData.inspections,
    targetCdf,
    targetGroup,
    workflowModel.componentDataFrames,
  ])
  const comparisonGroup = useMemo(
    () =>
      comparisonGroups.find(
        (group) => group.inspectionId === comparisonInspectionId
      ) ?? comparisonGroups[0],
    [comparisonGroups, comparisonInspectionId]
  )
  const comparisonCdf = useMemo(() => {
    if (!comparisonGroup || !targetCdf) return undefined
    return (
      getInspectionCdfsForResolutionKey(
        [comparisonGroup],
        getCdfResolutionKey(targetCdf)
      )[0]?.cdf ?? comparisonGroup.cdfs[0]
    )
  }, [comparisonGroup, targetCdf])
  const existingRateMatch = useMemo(() => {
    if (!targetGroup || comparisonGroups.length === 0) return undefined
    const resultCdfs = [
      ...workflowModel.generatedResultCdfs,
      ...workflowModel.componentDataFrames,
    ]
    let bestMatch:
      | {
          resultCdf: GeckoComponentDataFrame
          targetCdf: GeckoComponentDataFrame
          comparisonInspectionId: string
        }
      | undefined

    for (const candidateTargetCdf of targetGroup.cdfs) {
      if (!isInspectionThicknessCDF(candidateTargetCdf)) continue

      for (const candidateComparisonGroup of comparisonGroups) {
        for (const candidateComparisonCdf of candidateComparisonGroup.cdfs) {
          const resultCdf = findSessionShortTermCorrosionCdf(
            resultCdfs,
            candidateTargetCdf,
            candidateComparisonCdf
          )
          if (
            resultCdf &&
            (!bestMatch ||
              resultCdf.generation > bestMatch.resultCdf.generation)
          ) {
            bestMatch = {
              resultCdf,
              targetCdf: candidateTargetCdf,
              comparisonInspectionId: candidateComparisonGroup.inspectionId,
            }
          }
        }
      }
    }
    return bestMatch
  }, [
    comparisonGroups,
    targetGroup,
    workflowModel.componentDataFrames,
    workflowModel.generatedResultCdfs,
  ])
  const forecastComponentConfigQuery = useGetForecastComponentConfig(
    componentId ?? null
  )
  const forecastComponentConfigEntry = forecastComponentConfigQuery.entries[0]
  const forecastComponentConfig = forecastComponentConfigEntry?.data
  const tminMethod = useMemo(
    () =>
      componentId
        ? getTMinMethod(
            referenceData.assetModel,
            componentId,
            targetCdf,
            forecastComponentConfig
          )
        : undefined,
    [componentId, forecastComponentConfig, referenceData.assetModel, targetCdf]
  )
  const remainingLifeTminSource = useMemo<TminSource | undefined>(
    () => (tminMethod ? createTminSource({ tminMethod }) : undefined),
    [tminMethod]
  )
  const targetThicknessCdfs = useMemo(
    () => targetGroup?.cdfs ?? [],
    [targetGroup?.cdfs]
  )
  const targetDifferenceFromNominalCdfs = useGenerateLossByPercentageCDFs(
    referenceData.assetModel,
    targetThicknessCdfs,
    referenceData.inspections,
    componentId,
    undefined
  )
  const targetDifferenceFromNominalCdf = useMemo(() => {
    if (!targetCdf) return undefined
    const targetResolutionKey = getCdfResolutionKey(targetCdf)
    return targetDifferenceFromNominalCdfs.find(
      (cdf) => getCdfResolutionKey(cdf) === targetResolutionKey
    )
  }, [targetCdf, targetDifferenceFromNominalCdfs])
  const targetGrid = useMemo(
    () => (targetCdf ? getFeatureGridMetadata(targetCdf) : undefined),
    [targetCdf]
  )
  const comparisonGrid = useMemo(
    () => (comparisonCdf ? getFeatureGridMetadata(comparisonCdf) : undefined),
    [comparisonCdf]
  )
  const observationRectangles = useMemo(() => {
    if (!componentId || !targetGroup || !targetGrid) return []
    return getObservationFeatureRectangles({
      componentDeficiencies,
      componentId,
      inspectionId: targetGroup.inspectionId,
      targetGrid,
    })
  }, [componentDeficiencies, componentId, targetGrid, targetGroup])
  const existingRateCdf = useMemo(
    () =>
      findSessionShortTermCorrosionCdf(
        [
          ...workflowModel.generatedResultCdfs,
          ...workflowModel.componentDataFrames,
        ],
        targetCdf,
        comparisonCdf
      ) ?? existingRateMatch?.resultCdf,
    [
      comparisonCdf,
      existingRateMatch?.resultCdf,
      targetCdf,
      workflowModel.componentDataFrames,
      workflowModel.generatedResultCdfs,
    ]
  )
  const rateCdf = lastRateResult?.resultCdf ?? existingRateCdf
  const selectedFeature = useMemo(
    () =>
      featureRectangles.find(
        (featureRectangle) => featureRectangle.id === selectedFeatureId
      ) ?? featureRectangles[0],
    [featureRectangles, selectedFeatureId]
  )
  const selectedSupport = useMemo(
    () =>
      priorSupports.find(
        (support) => support.featureId === selectedFeature?.id
      ),
    [priorSupports, selectedFeature]
  )
  const resetDerivedState = useCallback(
    (nextObservationRectangles = observationRectangles) => {
      setStep(WizardStep.Features)
      setFeatureRectangles([...nextObservationRectangles])
      setPriorSupports([])
      setAlgorithmPriorSupports([])
      setSelectedFeatureId(nextObservationRectangles[0]?.id)
      setHasDetected(false)
      setHasMatched(false)
      setLastRateResult(null)
      setHoveredCoords(undefined)
      setError(null)
    },
    [observationRectangles]
  )

  useEffect(() => {
    const didOpen = active && !wasOpenRef.current
    wasOpenRef.current = active
    if (!didOpen) return
    setComponentId(
      initialComponentId ?? workflowModel.defaultPair?.component.id
    )
    setTargetInspectionId(
      initialTargetInspectionId ??
        workflowModel.defaultPair?.target.inspectionId
    )
    setTargetCdfId(
      initialTargetCdfId ?? workflowModel.defaultPair?.target.cdf.id
    )
    setComparisonInspectionId(
      workflowModel.defaultPair?.comparison.inspectionId
    )
    setParams(DEFAULT_SHORT_TERM_CORROSION_RATE_GENERATION_PARAMS)
    setAsyncStatus('idle')
    setError(null)
    setLastRateResult(null)
  }, [
    active,
    initialComponentId,
    initialTargetCdfId,
    initialTargetInspectionId,
    workflowModel.defaultPair,
  ])
  const inputSignature = [
    componentId,
    targetGroup?.inspectionId,
    targetCdf?.id,
    comparisonGroup?.inspectionId,
    comparisonCdf?.id,
  ].join(':')
  const resetSignature = [
    inputSignature,
    observationRectangles.map((rectangle) => rectangle.id).join(','),
  ].join('|')

  useEffect(() => {
    if (!active) {
      lastResetSignatureRef.current = undefined
      return
    }
    if (lastResetSignatureRef.current === resetSignature) return
    lastResetSignatureRef.current = resetSignature
    resetDerivedState(observationRectangles)
  }, [active, observationRectangles, resetDerivedState, resetSignature])

  const updateParams = useCallback(
    (updates: Partial<ShortTermCorrosionRateGenerationParams>) => {
      setParams((current) => ({ ...current, ...updates }))
      resetDerivedState()
    },
    [resetDerivedState]
  )
  const canDetect =
    !!targetCdf &&
    !!targetDifferenceFromNominalCdf &&
    !!targetGrid &&
    asyncStatus === 'idle'
  const canFindMatches =
    featureRectangles.length > 0 &&
    !!targetCdf &&
    !!comparisonCdf &&
    asyncStatus === 'idle'
  const canCalculateRate =
    featureRectangles.length > 0 &&
    !!targetCdf &&
    !!comparisonCdf &&
    !!targetDifferenceFromNominalCdf &&
    !!comparisonGroup &&
    hasMatched &&
    asyncStatus === 'idle'
  const canCalculateRemainingLifeFromRate = (
    shortTermRateCdf: GeckoComponentDataFrame | undefined
  ) =>
    !!shortTermRateCdf &&
    !!targetCdf &&
    !!targetGroup &&
    !!comparisonCdf &&
    !!comparisonGroup &&
    !!referenceData.assetModel &&
    !!componentId &&
    !!remainingLifeTminSource &&
    !forecastComponentConfigQuery.isLoading
  const persistReusableWorkflowCdf = async (
    cdf: GeckoComponentDataFrame,
    binnedDataBuffer: GeneratedShortTermCorrosionWorkflowCdfResult['binnedDataBuffer'],
    kind: CanonicalShortTermConditionLayerKind
  ) => {
    const reusableCdf = withReusableWorkflowGeneratedCdfMetadata(cdf, {
      displayName: getReusableWorkflowLayerName(cdf),
    })
    const canonicalCdf = withCanonicalShortTermConditionLayerMetadata(
      reusableCdf,
      kind
    )
    return persistGeneratedCdf(canonicalCdf, binnedDataBuffer)
  }
  const calculateRemainingLifeForRate = async ({
    shortTermRateCdf,
  }: {
    shortTermRateCdf: GeckoComponentDataFrame
  }) => {
    if (
      !canCalculateRemainingLifeFromRate(shortTermRateCdf) ||
      !targetCdf ||
      !targetGroup ||
      !comparisonCdf ||
      !comparisonGroup ||
      !referenceData.assetModel ||
      !componentId ||
      !remainingLifeTminSource
    ) {
      return undefined
    }
    const result = await calculateRemainingLifeCdf({
      targetCdf,
      targetInspection: targetGroup.inspection,
      comparisonCdf,
      comparisonInspection: comparisonGroup.inspection,
      shortTermRateCdf,
      assetModel: referenceData.assetModel,
      componentId,
      forecastComponentConfig,
      forecastComponentConfigGeneration:
        forecastComponentConfigEntry?.generation,
      tminSource: remainingLifeTminSource,
    })
    const persistedCdf = await persistReusableWorkflowCdf(
      result.resultCdf,
      result.binnedDataBuffer,
      CanonicalShortTermConditionLayerKind.ShortTermRemainingLife
    )
    const nextResult = { ...result, resultCdf: persistedCdf }
    await workflowModel.refresh()
    onGenerated(nextResult)
    return nextResult
  }
  const handleDetect = async () => {
    if (!targetCdf || !targetDifferenceFromNominalCdf) return
    setAsyncStatus('running')
    setError(null)
    try {
      const result = await detectFeatureRectangles({
        targetCdf,
        targetDifferenceFromNominalCdf,
        observationRectangles,
        params,
      })
      setFeatureRectangles(result.featureRectangles)
      setSelectedFeatureId(result.featureRectangles[0]?.id)
      setPriorSupports([])
      setAlgorithmPriorSupports([])
      setHasMatched(false)
      setHasDetected(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Feature detection failed.')
    } finally {
      setAsyncStatus('idle')
    }
  }
  const handleDeleteFeature = (featureId: string) => {
    setFeatureRectangles((current) => {
      const next = current.filter((rectangle) => rectangle.id !== featureId)
      setSelectedFeatureId((selectedId) =>
        selectedId === featureId ? next[0]?.id : selectedId
      )
      return next
    })
    setPriorSupports([])
    setAlgorithmPriorSupports([])
    setHasMatched(false)
    setLastRateResult(null)
    setStep(WizardStep.Features)
  }
  const handlePriorSupportShiftChange = useCallback(
    ({
      featureId,
      shiftXFeet,
      shiftYFeet,
    }: {
      featureId: string
      shiftXFeet: number
      shiftYFeet: number
    }) => {
      const featureRectangle = featureRectangles.find(
        (candidate) => candidate.id === featureId
      )
      setPriorSupports((current) =>
        current.map((support) => {
          if (support.featureId !== featureId) return support
          const shiftedPriorCellBounds =
            featureRectangle && targetGrid && comparisonGrid
              ? getShiftedRectangleCellBounds({
                  targetCellBounds: featureRectangle.targetCellBounds,
                  targetGrid,
                  comparisonGrid,
                  shiftXFeet,
                  shiftYFeet,
                })
              : undefined
          return {
            ...support,
            shiftXFeet,
            shiftYFeet,
            priorCellBounds: shiftedPriorCellBounds ?? support.priorCellBounds,
          }
        })
      )
      setLastRateResult(null)
    },
    [comparisonGrid, featureRectangles, targetGrid]
  )
  const handlePriorSupportShiftReset = useCallback(
    (featureId: string) => {
      const algorithmSupport = algorithmPriorSupports.find(
        (support) => support.featureId === featureId
      )
      if (!algorithmSupport) return
      setPriorSupports((current) =>
        current.map((support) =>
          support.featureId === featureId ? algorithmSupport : support
        )
      )
      setLastRateResult(null)
    },
    [algorithmPriorSupports]
  )
  const handleFindMatches = async () => {
    if (!targetCdf || !comparisonCdf || featureRectangles.length === 0) return
    setAsyncStatus('running')
    setError(null)
    try {
      const result = await matchFeatureRectangles({
        targetCdf,
        comparisonCdf,
        featureRectangles,
        params,
      })
      setPriorSupports(result.priorSupports)
      setAlgorithmPriorSupports(result.priorSupports)
      setSelectedFeatureId(featureRectangles[0]?.id)
      setHasMatched(true)
      setStep(WizardStep.Matches)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Feature matching failed.')
    } finally {
      setAsyncStatus('idle')
    }
  }
  const handleCalculateRate = async () => {
    if (
      !targetCdf ||
      !comparisonCdf ||
      !targetDifferenceFromNominalCdf ||
      !targetGroup ||
      !comparisonGroup
    ) {
      return
    }
    setAsyncStatus('running')
    setError(null)
    try {
      const result = await calculateRateCdf({
        targetCdf,
        targetInspection: targetGroup.inspection,
        comparisonCdf,
        comparisonInspection: comparisonGroup.inspection,
        targetDifferenceFromNominalCdf,
        featureRectangles,
        priorSupports,
        params,
      })
      const persistedCdf = await persistReusableWorkflowCdf(
        result.resultCdf,
        result.binnedDataBuffer,
        CanonicalShortTermConditionLayerKind.ShortTermCorrosionRate
      )
      const nextResult = { ...result, resultCdf: persistedCdf }
      await workflowModel.refresh()
      setLastRateResult(nextResult)
      setStep(WizardStep.Rate)
      if (canCalculateRemainingLifeFromRate(nextResult.resultCdf)) {
        try {
          await calculateRemainingLifeForRate({
            shortTermRateCdf: nextResult.resultCdf,
          })
        } catch (remainingLifeError) {
          setError(
            remainingLifeError instanceof Error
              ? `Short-term rate generated, but remaining life refresh failed. ${remainingLifeError.message}`
              : 'Short-term rate generated, but remaining life refresh failed.'
          )
        }
      }
      onGenerated(nextResult)
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Short-term corrosion rate calculation failed.'
      )
    } finally {
      setAsyncStatus('idle')
    }
  }
  const handleSelectTargetResolution = useCallback(
    (cdf: GeckoComponentDataFrame) => {
      const selectedResolutionKey = getCdfResolutionKey(cdf)
      const nextTargetCdf =
        targetGroup?.cdfs.find(
          (targetGroupCdf) =>
            getCdfResolutionKey(targetGroupCdf) === selectedResolutionKey
        ) ??
        targetGroup?.cdfs.find((targetGroupCdf) => targetGroupCdf.id === cdf.id)
      if (!nextTargetCdf) return
      setTargetCdfId(nextTargetCdf.id)
    },
    [targetGroup?.cdfs]
  )
  const handleCompleteRate = () => {
    if (!lastRateResult) return
    onRateComplete(lastRateResult)
  }
  const handleBack = () => {
    if (step === WizardStep.Features) {
      onExit()
      return
    }
    setStep(step === WizardStep.Rate ? WizardStep.Matches : WizardStep.Features)
  }
  const primaryAction = getShortTermWorkflowPrimaryAction({
    step,
    hasRateCdf: !!rateCdf,
    hasLastRateResult: !!lastRateResult,
    canFindMatches,
    canCalculateRate,
    onFindMatches: handleFindMatches,
    onViewRate: () => setStep(WizardStep.Rate),
    onCalculateRate: handleCalculateRate,
    onCompleteRate: handleCompleteRate,
  })
  const {
    selectedFeatureRectangles,
    selectedPriorRectangles,
    selectedPriorZoomBoundsFeet,
  } = getShortTermWorkflowFeatureViewState({
    selectedFeature,
    selectedSupport,
    comparisonGrid,
  })
  return (
    <ShortTermCorrosionWorkflowPanelView
      referenceData={referenceData}
      assetModel={referenceData.assetModel}
      componentId={componentId}
      step={step}
      error={error}
      asyncStatus={asyncStatus}
      targetGroup={targetGroup}
      comparisonGroup={comparisonGroup}
      targetGroups={targetGroups}
      comparisonGroups={comparisonGroups}
      targetCdf={targetCdf}
      comparisonCdf={comparisonCdf}
      targetDifferenceFromNominalCdf={targetDifferenceFromNominalCdf}
      targetDifferenceFromNominalCdfs={targetDifferenceFromNominalCdfs}
      rateDisplayCdf={lastRateResult?.resultCdf ?? existingRateCdf ?? targetCdf}
      featureRectangles={featureRectangles}
      selectedFeatureId={selectedFeature?.id}
      selectedFeatureRectangles={selectedFeatureRectangles}
      selectedPriorRectangles={selectedPriorRectangles}
      selectedFeatureZoomBoundsFeet={selectedFeature?.targetBoundsFeet}
      selectedPriorZoomBoundsFeet={selectedPriorZoomBoundsFeet}
      priorSupports={priorSupports}
      algorithmPriorSupports={algorithmPriorSupports}
      observationCount={observationRectangles.length}
      hasDetected={hasDetected}
      canDetect={canDetect}
      params={params}
      displayUnits={displayUnits}
      tminMethod={tminMethod}
      hoveredCoords={hoveredCoords}
      forecastComponentConfig={forecastComponentConfig}
      primaryAction={primaryAction}
      onTargetChange={(value) => {
        setTargetInspectionId(value)
        setTargetCdfId(undefined)
        setComparisonInspectionId(undefined)
      }}
      onComparisonChange={setComparisonInspectionId}
      onSelectTargetResolution={handleSelectTargetResolution}
      onDetect={handleDetect}
      onSelectFeature={setSelectedFeatureId}
      onDeleteFeature={handleDeleteFeature}
      onParamChange={updateParams}
      onSupportShiftChange={handlePriorSupportShiftChange}
      onSupportShiftReset={handlePriorSupportShiftReset}
      onBack={handleBack}
      onHoveredCoordsChange={setHoveredCoords}
    />
  )
}
